import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { Skeleton, useMediaQuery, useTheme } from "@mui/material"; // Import useMediaQuery and useTheme
import { headingFonts } from "../../../Styles/Styles";

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyUsers = () => {
  const [chartData, setChartData] = useState(null);
  const [maxYValue, setMaxYValue] = useState(null); // State for max y-axis value
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detect mobile screen size

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/charts/monthly-users`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token here
            },
          }
        );

        if (response.status === 401) {
          // Token invalid, redirect to login
          setError("Unauthorized. Please log in.");
          navigate("/get-started");
          return;
        }

        if (response.status === 403) {
          // Forbidden, show error message
          setError(
            "Access denied. You don't have permission to view this data."
          );
          return;
        }

        const data = await response.json();

        // Prepare chart data
        const labels = data.map((stat) => stat.monthYear); // Use monthYear for labels
        const counts = data.map((stat) => stat.count);

        // Calculate max y-axis value (2x of the maximum value in the dataset)
        const totalMax = Math.max(...counts);
        setMaxYValue(totalMax * 2);

        setChartData({
          labels,
          datasets: [
            {
              label: "Users Registered",
              data: counts,
              backgroundColor: "rgba(153, 102, 255, 0.6)", // Color for the user registration bar
              borderColor: "rgba(153, 102, 255, 1)",
              borderWidth: 1,
            },
          ],
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
        setError("An error occurred while fetching data.");
      }
    };

    fetchStats();
  }, [navigate]);

  return (
    <div style={{ width: "100%", padding: isMobile ? "10px" : "20px" }}>
      <h4 style={{ ...headingFonts, textAlign: "center" }}>
        Monthly User Registration Statistics
      </h4>
      {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

      {/* Skeleton Loader */}
      {!chartData && !error ? (
        <Skeleton variant="rectangular" width="100%" height={400} />
      ) : (
        chartData && (
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: isMobile ? "bottom" : "top",
                  labels: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                title: {
                  display: true,
                  text: "Number of Users Registered Per Month-Year",
                  font: {
                    family: "ClashGrotesk-Regular",
                  },
                  letterSpacing: 1,
                },
              },
              scales: {
                x: {
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
                y: {
                  beginAtZero: true,
                  max: maxYValue,
                  ticks: {
                    font: {
                      family: "ClashGrotesk-Medium",
                    },
                    letterSpacing: 1,
                  },
                },
              },
            }}
            style={{
              ...headingFonts,
              maxHeight: isMobile ? "300px" : "500px",
            }}
          />
        )
      )}
    </div>
  );
};

export default MonthlyUsers;
